import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@/lib/i18n';
import type { ImageRef } from '@/lib/types';
import { cn } from '@/lib/utils';
import { SmartImage } from './SmartImage';

/** Responsive photo grid with an accessible keyboard-navigable lightbox. */
export function PhotoGrid({ images, columns = 4, aspect = 'square', layout = 'grid' }: { images: ImageRef[]; columns?: number; aspect?: 'square' | 'landscape' | 'portrait' | 'auto'; layout?: 'grid' | 'masonry' | 'carousel' }) {
  const [index, setIndex] = useState<number | null>(null);
  const { L, t } = useI18n();
  const aspectClass = { square: 'aspect-square', landscape: 'aspect-[4/3]', portrait: 'aspect-[3/4]', auto: '' }[aspect];
  const close = useCallback(() => setIndex(null), []);
  const step = useCallback((d: number) => setIndex((i) => (i === null ? null : (i + d + images.length) % images.length)), [images.length]);
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [index, close, step]);
  if (!images.length) return null;
  const current = index !== null ? images[index] : null;
  const containerClass =
    layout === 'carousel' ? 'flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 scrollbar-none' :
    layout === 'masonry' ? cn('columns-2 gap-3 [&>*]:mb-3', columns >= 3 && 'md:columns-3', columns >= 4 && 'lg:columns-4') :
    cn('grid grid-cols-2 gap-3', columns >= 3 && 'md:grid-cols-3', columns >= 4 && 'lg:grid-cols-4');
  return (
    <>
      <div className={containerClass}>
        {images.map((img, i) => (
          <button key={`${img.url}-${i}`} type="button" onClick={() => setIndex(i)} className={cn('group relative block w-full overflow-hidden rounded-xl bg-surface-sunken text-left focus-visible:ring-2', layout === 'carousel' && 'w-64 shrink-0 snap-start', layout !== 'masonry' && aspectClass)} aria-label={`Open photo ${i + 1}${L(img.caption) ? `: ${L(img.caption)}` : ''}`}>
            <SmartImage image={img} rounded={false} className={cn('w-full transition duration-500 group-hover:scale-[1.04]', layout !== 'masonry' && 'h-full')} sizes="(max-width: 768px) 50vw, 25vw" />
            {(L(img.caption) || img.credit) && (
              <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-xs text-white opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
                {L(img.caption)}
              </span>
            )}
          </button>
        ))}
      </div>
      {current && (
        <div role="dialog" aria-modal="true" aria-label="Photo viewer" className="fixed inset-0 z-[60] flex flex-col bg-black/95 animate-fade-in" onClick={close}>
          <div className="flex items-center justify-between p-3 text-white/80">
            <span className="text-sm">{index! + 1} / {images.length}</span>
            <button type="button" onClick={close} aria-label={t('close')} className="rounded-full p-2 hover:bg-white/10"><X className="size-6" /></button>
          </div>
          <div className="relative flex min-h-0 flex-1 items-center justify-center px-2" onClick={(e) => e.stopPropagation()}>
            {images.length > 1 && <button type="button" onClick={() => step(-1)} aria-label={t('previous')} className="absolute left-2 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><ChevronLeft className="size-6" /></button>}
            <img src={current.url} alt={L(current.alt) || L(current.caption)} className="max-h-full max-w-full object-contain" />
            {images.length > 1 && <button type="button" onClick={() => step(1)} aria-label={t('next')} className="absolute right-2 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><ChevronRight className="size-6" /></button>}
          </div>
          <div className="p-4 text-center text-sm text-white/90" onClick={(e) => e.stopPropagation()}>
            {L(current.caption)}
            {current.credit && <span className="ml-2 text-white/60">· {t('photoBy')}: {current.credit}</span>}
          </div>
        </div>
      )}
    </>
  );
}
